/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useEffect, useState } from 'react';
import { Box, Form, FormField, Text } from 'grommet';
import { Modal } from 'blb-components/GrommetComponents';
import { ModalContent } from 'blb-components/ModalContent';
import { isEmptyValue } from '@blend/enterprise-components';
import { Button, TextInput } from '@blend/alchemy';
import { CredentialReviewType } from 'blb-constants/ron';
import { addCredentialReview } from 'blb-api/ron';
import * as Sentry from '@sentry/browser';

interface FormValues {
  reason: string;
}

export namespace ReviewConfirmationModal {
  export interface Props {
    credentialId: string;
    handleClose: () => void;
    isOpen: boolean;
    principalId: string;
    reviewType: CredentialReviewType;
  }
}

export const ReviewConfirmationModal: React.FC<ReviewConfirmationModal.Props> = ({
  reviewType,
  handleClose,
  isOpen,
  principalId,
  credentialId,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>();
  const [formValues, setFormValues] = useState<FormValues>({
    reason: '',
  });
  const [isFormValid, setIsFormValid] = useState<boolean>(false);

  useEffect(() => {
    setIsFormValid(reviewType === CredentialReviewType.APPROVE || formValues.reason?.length > 0);
  }, [reviewType, formValues]);

  const handleSubmit = async (): Promise<void> => {
    try {
      await addCredentialReview(principalId, credentialId, reviewType, formValues.reason);
      cleanUpThenClose();
    } catch (error) {
      setErrorMessage('Failed to add review');
      Sentry.captureException(error);
    }
  };

  const submitButton = (
    <Button
      disabled={!isFormValid}
      id="it-reviewConfirmationModal-submit-btn"
      label={reviewType === 'APPROVE' ? 'Confirm Approval' : 'Confirm Rejection'}
      onClick={handleSubmit}
      size="medium"
      variant="primary"
    />
  );

  const createTextInputFormField = ({
    label,
    name,
    validator,
  }: {
    label: string;
    name: string;
    validator?: (value: string) => string | undefined;
  }): React.ReactElement => {
    return (
      <FormField
        id={`it-reviewConfirmationModal-${name}-field`}
        label={<Text size="small">{label}</Text>}
        name={name}
        validate={validator}
      >
        <TextInput
          data-it={`it-reviewConfirmationModal-reason-input-${name}`}
          id={`it-${name}`}
          label={label}
          name={name}
          showLabel={false}
          size="medium"
        />
      </FormField>
    );
  };

  const cleanUpThenClose = () => {
    setFormValues({ reason: '' });
    handleClose();
  };

  return (
    <Modal
      buttonComponents={submitButton}
      handleClose={cleanUpThenClose}
      isOpen={isOpen}
      title={reviewType === CredentialReviewType.APPROVE ? 'Approve credential' : 'Reject credential'}
    >
      <ModalContent headerText="">
        <Form onChange={setFormValues} onSubmit={handleSubmit} validate="change" value={formValues}>
          <Box width="medium">
            {createTextInputFormField({
              label: reviewType === CredentialReviewType.APPROVE ? 'Approval message' : 'Reason for rejection',
              name: 'reason',
              validator: reviewType === CredentialReviewType.APPROVE ? undefined : isEmptyValue(`Reason is required`),
            })}
          </Box>
        </Form>
        <Text color="accentRed" data-it="it-inviteNotaryModal-error">
          {errorMessage}
        </Text>
      </ModalContent>
    </Modal>
  );
};
